import React from "react";
import { useTranslations } from "next-intl";
import { useRef } from "react";
import { OptionInput } from "../../Inputs/OptionInput";

type TableCellContentBooleanProps = {
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  required: boolean;
  /** PB-RP-1: `undefined` = UNTOUCHED, `""` = CLEARED. The two are not the
   *  same and the component's own selection logic depends on telling them
   *  apart — see the `EMPTY_DISPLAY` branch below. */
  value: string | undefined;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  onFocusAction?: () => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  viewOnly?: boolean;
  testId?: string;
  /** Edit rows only: `false` drops the "—" not-set pick (a field the backend
   *  will not unset, e.g. the strict SMS consent). Filter rows keep it — "—"
   *  there reads "is empty", it writes nothing. Default `true`. */
  clearable?: boolean;
};

export const TableCellContentBoolean: React.FC<
  TableCellContentBooleanProps
> = React.memo(function TableCellContentBoolean({
  isFilter,
  border,
  editable,
  required,
  value: rawValue,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height,
  surface,
  fontSize,
  padding,
  rounded,
  viewOnly,
  testId,
  clearable = true,
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("boolean");
  // PB-RP-1: see TCCSingleSelect — raw for the sentinel, coerced downstream.
  const value = rawValue ?? "";
  const boolean =
    required && !isFilter
      ? value === "true"
        ? "true"
        : "false"
      : value === "true"
        ? "true"
        : value === "false"
          ? "false"
          : "";

  // Non-required edit rows AND every filter row expose a "—" not-set option.
  // Edit: clears the value. Filter: "—" + "=" is translated to the "∅" (is
  // empty) operator at serialize time (see coerceEmptyEqualsOperator in
  // parser.ts), so it matches null/unset rows instead of literal-"".
  // Its *value* is the em dash itself (a real, non-empty string), NOT "": ""
  // would collide with OptionInput's empty `inputValue` and its
  // `value === inputValue` guard would dead-click the option whenever the field
  // is unset (the common case in bulk-edit). setParent maps "—" back to "".
  const showEmptyOption = isFilter || (!required && clearable);
  const EMPTY_DISPLAY = "—";
  // Distinguish "cleared" ("") — show "—" selected — from "untouched"
  // (undefined) — show nothing yet, so bulk-edit's pick-first gate still holds.
  const defaultValue =
    boolean !== ""
      ? t(boolean)
      // PB-RP-1: `rawValue`, not the coerced one — undefined is UNTOUCHED and
      // must NOT preselect the sentinel, or the first click on "—" is a
      // self-select that never fires onChange.
      : showEmptyOption && rawValue === ""
        ? EMPTY_DISPLAY
        : "";

  return (
    <>
      {isEditing && isSelected && (editable || isFilter) ? (
        <div className="flex flex-col flex-1">
          <OptionInput
            height={height}
            surface={surface}
            disabled={viewOnly}
            fontSize={fontSize}
            padding={padding}
            rounded={rounded}
            isFilter={isFilter}
            onFocusAction={onFocusAction}
            border={border}
            inputRef={inputRef}
            setParent={(value) => {
              onChangeValue(
                value === t("true")
                  ? "true"
                  : value === t("false")
                    ? "false"
                    : "",
              );
            }}
            options={
              // Filter rows + non-required edit rows expose the "—" not-set
              // pick. In filters it serializes to the "∅" (is empty) operator
              // so it matches unset rows; required edit rows omit it.
              showEmptyOption
                ? [t("true"), t("false"), EMPTY_DISPLAY]
                : [t("true"), t("false")]
            }
            defaultValue={defaultValue}
            required={required}
            testId={testId}
          />
          {error && <p className="mt-1 body-3-regular opacity-0">{error}</p>}
        </div>
      ) : (
        <p>{boolean !== "" ? t(boolean) : ""}</p>
      )}
    </>
  );
});
